/**
 * WVB-04 — Financial Dashboard
 * Route: /finance/dashboard
 * Roles: FINANCE_APPROVER, TENANT_ADMIN, PROJECT_MANAGER (own events)
 * Capability: financial.dashboard.view
 *
 * Shows multi-event financial summary: revenue vs. approved budget,
 * budgeted gross margin, financial health, and pending revisions.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  CircleDashed, RefreshCw, Loader2, ChevronRight,
  Filter, Calendar, Search, AlertCircle, Clock,
  ArrowUpRight, BarChart2, DollarSign, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { financialApi } from "@/services/api";
import { toast } from "sonner";

// ── Health badge config ───────────────────────────────────────────────────────

const HEALTH_CONFIG = {
  HEALTHY:                    { icon: CheckCircle,   color: "text-[#2E844A]",  bg: "bg-[#EAF5EE]",  label: "Healthy" },
  MARGIN_WARNING:             { icon: AlertTriangle, color: "text-[#8C4B00]",  bg: "bg-[#FFF4E5]",  label: "Warning" },
  MARGIN_CRITICAL:            { icon: AlertTriangle, color: "text-[#BA0517]",  bg: "bg-[#FCEBEC]",  label: "Critical" },
  BUDGET_EXCEEDS_CONTRACT:    { icon: TrendingDown,  color: "text-[#BA0517]",  bg: "bg-[#FCEBEC]",  label: "Exceeds Contract" },
  NO_APPROVED_BUDGET:         { icon: CircleDashed,  color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]",  label: "No Budget" },
  MARGIN_POLICY_NOT_CONFIGURED: { icon: CircleDashed, color: "text-[#706E6B]", bg: "bg-[#F3F3F3]",  label: "No Policy" },
  BOOKING_NOT_FOUND:          { icon: CircleDashed,  color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]",  label: "No Booking" },
  BOOKING_CANCELLED:          { icon: AlertCircle,   color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]",  label: "Cancelled" },
  CURRENCY_MISMATCH:          { icon: AlertTriangle, color: "text-[#8C4B00]",  bg: "bg-[#FFF4E5]",  label: "Currency Mismatch" },
  REVENUE_UNAVAILABLE:        { icon: AlertCircle,   color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]",  label: "Revenue N/A" },
  DATA_UNAVAILABLE:           { icon: AlertCircle,   color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]",  label: "N/A" },
};

function HealthBadge({ code }) {
  const cfg = HEALTH_CONFIG[code] || HEALTH_CONFIG.DATA_UNAVAILABLE;
  const Icon = cfg.icon;
  return (
    <span data-testid={`health-badge-${code}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color}`}>
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}

// ── KPI summary card ──────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className={`bg-white border border-[#E5E5E5] rounded-xl p-4 flex items-start gap-3`}>
      <div className={`p-2 rounded-lg ${accent}`}>
        <Icon size={16} className="text-white" />
      </div>
      <div>
        <p className="text-xs text-[#706E6B] font-medium">{label}</p>
        <p className="text-lg font-bold text-[#1C1917] leading-tight">{value}</p>
        {sub && <p className="text-xs text-[#706E6B] mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

export default function FinancialDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [healthFilter, setHealthFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const canView = hasCapability(user, "financial.dashboard.view");

  const load = useCallback(async (pg = 1, hf = "") => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await financialApi.getDashboard({ page: pg, page_size: 20, health_filter: hf || undefined });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load financial dashboard");
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => { load(1, healthFilter); }, [healthFilter]);

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64 text-[#706E6B]" data-testid="financial-dashboard-forbidden">
        <AlertCircle size={20} className="mr-2" />
        You do not have access to the Financial Dashboard.
      </div>
    );
  }

  const summary = data?.summary || {};
  const events = data?.events || [];
  const pagination = data?.pagination || {};

  // Local search filter
  const filtered = searchTerm
    ? events.filter(e =>
        (e.event_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (e.event_number || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : events;

  return (
    <div className="min-h-screen bg-[#F8F6F1] p-4 sm:p-6" data-testid="financial-dashboard">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1917] flex items-center gap-2">
            <BarChart2 size={22} className="text-[#0176D3]" />
            Financial Dashboard
          </h1>
          <p className="text-sm text-[#706E6B] mt-0.5">
            Budgeted gross margin · Revenue vs. approved cost
          </p>
        </div>
        <button
          data-testid="financial-dashboard-refresh"
          onClick={() => load(page, healthFilter)}
          className="flex items-center gap-1.5 text-sm text-[#0176D3] hover:text-[#014486] font-medium px-3 py-1.5 rounded-lg hover:bg-[#0176D3]/10 transition-colors"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* KPI row */}
      {!loading && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="financial-dashboard-kpis">
          <KpiCard label="Total Events" value={summary.total_events ?? "—"} icon={Calendar} accent="bg-[#0176D3]" />
          <KpiCard label="Healthy" value={summary.healthy_count ?? 0} icon={CheckCircle} accent="bg-[#2E844A]" sub="≥ min margin" />
          <KpiCard label="Warning / Critical" value={(summary.warning_count ?? 0) + (summary.critical_count ?? 0)} icon={AlertTriangle} accent="bg-[#8C4B00]" sub="Below threshold" />
          <KpiCard label="No Budget" value={summary.no_budget_count ?? 0} icon={CircleDashed} accent="bg-[#706E6B]" sub="Awaiting budget" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" />
          <input
            data-testid="financial-dashboard-search"
            type="text"
            placeholder="Search by event name or number…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#D8D8D8] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0176D3]/20 bg-white"
          />
        </div>
        <div className="relative">
          <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" />
          <select
            data-testid="financial-dashboard-health-filter"
            value={healthFilter}
            onChange={e => { setHealthFilter(e.target.value); setPage(1); }}
            className="pl-9 pr-6 py-2 text-sm border border-[#D8D8D8] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0176D3]/20 appearance-none cursor-pointer"
          >
            <option value="">All health states</option>
            <option value="HEALTHY">Healthy</option>
            <option value="MARGIN_WARNING">Warning</option>
            <option value="MARGIN_CRITICAL">Critical</option>
            <option value="BUDGET_EXCEEDS_CONTRACT">Exceeds Contract</option>
            <option value="NO_APPROVED_BUDGET">No Approved Budget</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid="financial-dashboard-table">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={20} className="animate-spin text-[#0176D3]" />
            <span className="ml-2 text-sm text-[#706E6B]">Loading financial data…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-[#706E6B]">
            <DollarSign size={32} className="mx-auto mb-3 opacity-40" />
            <p className="font-medium">No events found</p>
            <p className="text-sm mt-1">Try adjusting your filters or create a budget for an event</p>
          </div>
        ) : (
          <>
            {/* Table header */}
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_40px] gap-2 px-4 py-2.5 bg-[#F8F6F1] border-b border-[#E5E5E5] text-xs font-semibold text-[#706E6B] uppercase tracking-wide">
              <span>Event</span>
              <span className="text-right">Contract Revenue</span>
              <span className="text-right">Approved Budget</span>
              <span className="text-right">Gross Margin</span>
              <span className="text-center">Health</span>
              <span />
            </div>
            {/* Rows */}
            {filtered.map(ev => (
              <EventFinancialRow
                key={ev.event_id}
                event={ev}
                onNavigate={() => navigate(`/crm/events/${ev.event_id}/financials`)}
              />
            ))}
          </>
        )}
      </div>

      {/* Pagination */}
      {!loading && pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-4" data-testid="financial-dashboard-pagination">
          <button
            disabled={page <= 1}
            onClick={() => { const p = page - 1; setPage(p); load(p, healthFilter); }}
            className="px-4 py-2 text-sm border border-[#D8D8D8] rounded-lg disabled:opacity-40 hover:bg-[#F3F3F3] transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-[#706E6B]">Page {page} of {pagination.total_pages}</span>
          <button
            disabled={page >= pagination.total_pages}
            onClick={() => { const p = page + 1; setPage(p); load(p, healthFilter); }}
            className="px-4 py-2 text-sm border border-[#D8D8D8] rounded-lg disabled:opacity-40 hover:bg-[#F3F3F3] transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ── Individual event row ──────────────────────────────────────────────────────

function EventFinancialRow({ event, onNavigate }) {
  const hasPending = event.has_pending_revision;

  return (
    <div
      data-testid={`financial-row-${event.event_id}`}
      className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_40px] gap-2 px-4 py-3 border-b border-[#F3F3F3] hover:bg-[#F8F6F1]/60 transition-colors items-center"
    >
      {/* Event info */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-[#1C1917] text-sm truncate">{event.event_name || "Unnamed Event"}</p>
          {hasPending && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#8C4B00] bg-[#FFF4E5] px-1.5 py-0.5 rounded-full whitespace-nowrap">
              <Clock size={9} />
              Rev. pending
            </span>
          )}
        </div>
        <p className="text-xs text-[#706E6B] mt-0.5">{event.event_number || ""}</p>
      </div>

      {/* Contract revenue */}
      <div className="text-right">
        <p className="text-sm font-medium text-[#1C1917]">
          {event.contract_revenue_display || <span className="text-[#706E6B] text-xs italic">N/A</span>}
        </p>
      </div>

      {/* Approved budget */}
      <div className="text-right">
        <p className="text-sm font-medium text-[#1C1917]">
          {event.approved_budget_display || <span className="text-[#706E6B] text-xs italic">None</span>}
        </p>
        {event.budget_version && (
          <p className="text-[10px] text-[#706E6B]">v{event.budget_version}</p>
        )}
      </div>

      {/* Gross margin */}
      <div className="text-right">
        {event.gross_margin_percent != null ? (
          <p className={`text-sm font-bold ${
            parseFloat(event.gross_margin_percent) < 0
              ? "text-[#BA0517]"
              : parseFloat(event.gross_margin_percent) < 10
              ? "text-[#8C4B00]"
              : "text-[#2E844A]"
          }`}>
            {event.gross_margin_percent}%
          </p>
        ) : (
          <span className="text-xs text-[#706E6B] italic">—</span>
        )}
      </div>

      {/* Health badge */}
      <div className="flex justify-center">
        <HealthBadge code={event.financial_health} />
      </div>

      {/* Nav arrow */}
      <button
        data-testid={`financial-row-nav-${event.event_id}`}
        onClick={onNavigate}
        className="p-1 rounded hover:bg-[#0176D3]/10 text-[#706E6B] hover:text-[#0176D3] transition-colors"
        title="Open event financials"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}
